import { useEffect, useMemo } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { useGetMeQuery } from "../../api/authApi";
import { useAuth } from "../../contexts/AuthContext";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import { makeMuiTheme } from "../../theme/theme";
import { darkColors } from "../../theme/tokens";
import LoadingView from "../../components/ui/LoadingView";
import SideNav from "./SideNav";
import AdminSideNav, { AdminMobileBar } from "./AdminSideNav";
import { ADMIN_HOME, USER_HOME, isAdmin } from "../../lib/adminFormat";
import ConnectionState from "../../components/ui/ConnectionState";

// Koyu zemin: mobildeki gibi koyu turkuazdan mürekkep turkuaza geçiş.
const DARK_BACKGROUND = `linear-gradient(160deg, ${darkColors.gradientFrom} 0%, ${darkColors.gradientTo} 55%)`;

/**
 * Uygulama kabı. `variant="admin"` Terakki Yönetimi bölümüdür (/sistem/*):
 * Admin kullanıcı Terakki kullanıcısı değil, sistem denetim hesabıdır; yalnız
 * yönetim menüsünü görür. Kullanıcı bölümüne (/uygulama/*) giren Admin panoya,
 * yönetim bölümüne giren Admin olmayan kullanıcı profil sayfasına gider.
 */
export default function AppShell({ variant = "user" }) {
  const { hasToken, user, setUser } = useAuth();
  const { data, error, isLoading, isFetching, refetch } = useGetMeQuery(undefined, { skip: !hasToken });
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const dark = mode === "dark";
  const theme = useMemo(() => makeMuiTheme(mode), [mode]);

  // Tema yalnız uygulama içinde: <html>'e "dark" sınıfı (Tailwind dark:) ve
  // gövde zemini. Uygulamadan çıkınca (giriş, tanıtım) açık tema geri gelir.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", dark);
    root.style.colorScheme = dark ? "dark" : "light";
    document.body.style.backgroundColor = dark ? darkColors.bg : "";
    return () => {
      root.classList.remove("dark");
      root.style.colorScheme = "";
      document.body.style.backgroundColor = "";
    };
  }, [dark]);

  useEffect(() => {
    if (data?.data?.user) setUser(data.data.user);
  }, [data, setUser]);

  useEffect(() => {
    if (error?.status === 401) {
      navigate("/giris", { replace: true });
    }
  }, [error, navigate]);

  if (isLoading && !user) return <LoadingView />;

  const me = user || data?.data?.user || null;
  const adminArea = variant === "admin";
  if (!adminArea && isAdmin(me)) return <Navigate to={ADMIN_HOME} replace />;
  if (adminArea) {
    // Rol bilinmeden yönetim sayfalarını göstermiyoruz (401'de yukarıdaki
    // yönlendirme devreye girer).
    if (!me && error && error.status !== 401) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6">
          <ConnectionState error={error} onRetry={refetch} retrying={isFetching} message={error.message} />
        </div>
      );
    }
    if (!me) return <LoadingView />;
    if (!isAdmin(me)) return <Navigate to={USER_HOME} replace />;
  }

  // Üst çubuk kaldırıldı: marka, gezinme ve hesap menüsü tek bir sol rayda.
  return (
    <ThemeProvider theme={theme}>
      <div
        className="min-h-screen flex bg-terakkiSurfaceAlt dark:text-white"
        style={dark ? { background: DARK_BACKGROUND, backgroundAttachment: "fixed" } : undefined}
      >
        <aside className="hidden md:block shrink-0 sticky top-0 h-screen">
          {adminArea ? <AdminSideNav /> : <SideNav />}
        </aside>
        {/* lg ve üstünde sayfa değil, sütunlar kendi içinde kayar; böylece
            kutular ekran boyunu aşmıyor. Küçük ekranda normal akış. */}
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:h-screen lg:overflow-y-auto">
          {adminArea && <AdminMobileBar />}
          <Outlet />
        </main>
      </div>
    </ThemeProvider>
  );
}
